"use client";

import { useState, useRef, useEffect } from "react";
import { countries, ICountry as Country } from "countries-list";
import { ChevronDown, Search } from "lucide-react";

interface SearchableCountrySelectProps {
  value: string;
  onChange: (countryCode: string, callingCode: string) => void;
  disabled?: boolean;
  className?: string;
}

// Helper function to get country flag emoji from country code
const getFlagEmoji = (countryCode: string): string => {
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

export default function SearchableCountrySelect({
  value,
  onChange,
  disabled = false,
  className = "",
}: SearchableCountrySelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Get the selected country details - handle undefined or invalid values
  const countryCode = value || "NG";
  const selectedCountry = countries[
    countryCode as keyof typeof countries
  ] as Country;

  // Generate display value with flag emoji
  let displayValue = "🇳🇬 +234"; // Default to Nigeria
  if (selectedCountry) {
    const flag = getFlagEmoji(countryCode);
    displayValue = `${flag} +${selectedCountry.phone[0]}`;
  }

  // Filter countries based on search term
  const filteredCountries = Object.entries(countries).filter(
    ([code, country]) => {
      const search = searchTerm.toLowerCase();
      const phoneStr = country.phone.join(",");
      return (
        country.name.toLowerCase().includes(search) ||
        phoneStr.includes(search) ||
        code.toLowerCase().includes(search)
      );
    }
  );

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (code: string, country: Country) => {
    onChange(code, `+${country.phone[0]}`);
    setIsOpen(false);
    setSearchTerm("");
  };

  if (disabled) {
    return (
      <div
        className={`border border-grey-200 rounded-lg bg-grey-50 font-medium text-grey-700 truncate ${
          className.includes("px-") ? className : `px-3 py-3 ${className}`
        }`}
      >
        {displayValue}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`border border-grey-200 rounded-lg bg-white font-medium text-grey-700 hover:bg-grey-50 focus:outline-none focus:ring-2 focus:ring-green-300 flex items-center gap-2 min-w-0 ${
          className.includes("px-") ? className : `px-3 py-3 ${className}`
        }`}
      >
        <span className="truncate">{displayValue}</span>
        <ChevronDown
          className={`h-4 w-4 transition-transform flex-shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-72 bg-white border border-grey-200 rounded-lg shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-grey-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search country or code..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-grey-100 rounded-md focus:outline-none focus:ring-2 focus:ring-green-300"
              />
            </div>
          </div>

          {/* Country List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredCountries.length > 0 ? (
              filteredCountries.map(([code, country]) => {
                const flag = getFlagEmoji(code);
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => handleSelect(code, country)}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-grey-50 flex items-center justify-between ${
                      code === countryCode
                        ? "bg-green-50 text-green-700"
                        : "text-grey-700"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-lg">{flag}</span>
                      <span>{country.name}</span>
                    </span>
                    <span className="text-grey-500">+{country.phone[0]}</span>
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-4 text-sm text-grey-500 text-center">
                No countries found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
